/**
 * The SDK's one zone loader (`ZoneLoad`): every SDK path that turns a stored
 * or reported IANA name into a `TimezoneRegion` goes through it, so a
 * backward link (`US/Pacific`) loads as its canonical zone everywhere
 * (`TimezoneRegion.getOpt` canonicalizes through the platform's Intl, and
 * loads the name itself when the platform's canonical spelling is not
 * served) and a name that does not load is never a silent UTC. A zone the
 * service serves with offsets the platform disagrees with loads rebuilt from
 * the platform's (`TimezoneRegion.getOpt`); the loader traces each such zone
 * once (`zone/offset-mismatch`, with both offsets: `TimezoneRegion.takeRebuilt`),
 * so stale zone data shows in the trace while the viewer sees the right time.
 * A zone the service has no data for (a 404 or no transitions) that the
 * platform knows loads built from the platform's offsets, traced once the
 * same way (`zone/unserved`, with the reason), so Pacific/Port_Moresby shows
 * +10:00 rather than a UTC fallback; a zone the service gave no answer for
 * (`TimezoneRegion.NoAnswer`) loads the same way, held for the retry window,
 * and is traced once (`zone/unserved`, `no-answer`). A zone data version the service does not
 * serve is traced once (`tz/data-version-fallback`) as the loader falls back
 * to the legacy data. A fallback chrono's own zone reads took
 * (`TimezoneRegion.get`: an iCal TZID, a recurrence rule's zone) is traced
 * `tz/zone-fallback` as this loader's own are (`report`).
 *
 * Every outcome also reaches chrono's outcome listeners
 * (`TimezoneRegion.onOutcome`): a fallback this loader takes is reported
 * there with the read that asked (`Source`), so each client's record
 * (`client.zones`, `ZoneOutcomes`) reads it typed; the traces stay as
 * telemetry.
 *
 * It sits in the rows-side core (layering rank 1) so every layer loads
 * zones through it: the events index's recurrence frame
 * (`EventItem.makeRecurrenceGenerator`), the timeline's flight and index
 * mappers, the events client and the preferences (through
 * `@tento-lona/sheets/internal-sdk`). A fallback is traced
 * (`tz/zone-fallback`, a trace entry a host or test can read) and returned
 * beside the zone (`resolve`).
 *
 * @module
 */
import type { Option } from "./option.ts";
import { Tracing } from "@tento-core/tracing";
import { TimezoneRegion, Tzname } from "@tento-chrono";

/** See the module note. */
export namespace ZoneLoad {
  /**
   * `name`'s zone (a link as its canonical zone), or `null` when it is not a
   * zone or does not load (a zone the service has no data for loads from the
   * platform when the platform knows it).
   */
  export async function opt(name: string): Promise<Option<TimezoneRegion>> {
    const tz = Tzname.parseOpt(name);
    const zone = tz == null ? null : await TimezoneRegion.getOpt(tz);
    report();
    return zone;
  }

  /**
   * Trace, once each, what chrono did with zones since the last report,
   * whoever loaded them: the zones it rebuilt or built from the platform
   * (`zone/offset-mismatch`, `zone/unserved`), and the fallbacks its own zone
   * reads took (`TimezoneRegion.takeFallbacks`: an iCal `TZID` or a
   * recurrence rule's zone that does not load is read in UTC), traced
   * `tz/zone-fallback` as `resolve` traces its own, and a zone data version
   * the service does not serve (`TimezoneRegion.takeVersionFallbacks`: the
   * loader reads the legacy data instead), traced `tz/data-version-fallback`. `opt` reports after every
   * load; a caller that hands chrono zone names itself (the recurrence parse)
   * reports after it.
   */
  export function report(): void {
    for (const rebuilt of TimezoneRegion.takeRebuilt()) {
      if (rebuilt.unserved != null) {
        Tracing.log("zone/unserved", { zone: String(rebuilt.fullname), reason: rebuilt.unserved });
        continue;
      }
      if (rebuilt.mismatch == null) continue;
      const { at, servedMins, platformMins } = rebuilt.mismatch;
      Tracing.log("zone/offset-mismatch", {
        zone: String(rebuilt.fullname),
        at: new Date(at).toISOString(),
        servedMins,
        platformMins,
      });
    }
    for (const { requested, used } of TimezoneRegion.takeFallbacks()) {
      Tracing.log("tz/zone-fallback", { name: String(requested), fallback: String(used) });
    }
    for (const { asked, used } of TimezoneRegion.takeVersionFallbacks()) {
      Tracing.log("tz/data-version-fallback", { asked, used });
    }
  }

  /**
   * `name`'s zone; when it does not load, `fallback`'s, then UTC — each step
   * traced (`tz/zone-fallback`, with both names), never silent. Beside the
   * zone, the fallback taken (`null`: `name` loaded): the name that did not
   * load and the zone used instead, for a host to tell the viewer. The
   * fallback taken is reported to chrono's outcome listeners with `source`,
   * the read that asked (`TimezoneRegion.reportFallback`); `null`: the caller
   * records it itself (the viewer's preferences, `peekZoneFallback`).
   */
  export async function resolve(
    name: string,
    fallback: Tzname,
    source: Option<ZoneLoad.Source>,
  ): Promise<ZoneLoad.Resolved> {
    const zone = await opt(name);
    if (zone != null) return { zone, fallback: null };
    Tracing.log("tz/zone-fallback", { name, fallback: String(fallback) });
    let used = await opt(fallback);
    if (used == null) {
      Tracing.log("tz/zone-fallback", { name: String(fallback), fallback: "UTC" });
      used = await TimezoneRegion.get(Tzname.UTC);
    }
    const taken = { requested: name, used: used.fullname };
    if (source != null) TimezoneRegion.reportFallback({ ...taken, source });
    return { zone: used, fallback: taken };
  }

  /**
   * The read a fallback came from: an event's zone, an iCal line's `TZID`
   * or a recurrence rule's zone (chrono's own reads), a ground index that
   * names no zone (the runtime's), a flight's zone, the home transition's
   * zone, the runtime's zone, or another caller. The same names as a
   * client's record publishes (`ZoneOutcomes.Source`, `client.zones`).
   */
  export type Source = "event" | "ical-tzid" | "rrule" | "ground-index" | "flight" | "home-zone" | "runtime" | "other";

  /** A zone and the fallback taken to reach it (`null`: the name asked for loaded). */
  export interface Resolved {
    readonly zone: TimezoneRegion;
    readonly fallback: Option<{ readonly requested: string; readonly used: Tzname }>;
  }

  /** `name`'s zone, falling back as `resolve` does (reported with `source`). */
  export async function orElse(name: string, fallback: Tzname, source: ZoneLoad.Source): Promise<TimezoneRegion> {
    return (await resolve(name, fallback, source)).zone;
  }
}
